/**
 * Lättviktig analytics-helper. Skriver events till window.dataLayer (GTM-kompatibel)
 * och console.debug i utveckling. Sväljer fel — får aldrig krascha UI.
 * Fritextfält skickas ALDRIG. Alla URL-liknande värden (page_location, href …)
 * maskeras via redactDeep så token_hash/token aldrig når GA4 eller dataLayer.
 */
import { redactDeep } from "./redact-url";
import { pageAccess } from "./gtm";

export type TrackEvent =
  | "school_b2b_page_viewed"
  | "school_b2b_video_started"
  | "school_b2b_video_completed"
  | "school_b2b_benefit_opened"
  | "school_b2b_profile_example_opened"
  | "school_b2b_pricing_viewed"
  | "school_b2b_plan_selected"
  | "school_b2b_profile_search_started"
  | "school_b2b_profile_found"
  // Profilanalysen: bara skolenhetskod, kommun och skolform — aldrig namn,
  // mejladress eller något annat som pekar ut en person.
  | "school_b2b_profile_opened"
  | "school_b2b_analysis_emailed"
  | "school_b2b_walkthrough_requested"
  | "school_b2b_profile_claim_started"
  | "school_b2b_demo_started"
  | "school_b2b_demo_submitted"
  | "school_b2b_contact_submitted"
  | "school_b2b_job_feature_opened"
  | "school_b2b_case_opened"
  | "school_b2b_faq_opened"
  | "school_b2b_action_checked"
  | "school_b2b_action_unchecked"
  | "career_stack_general_interest"
  | "career_stack_browse_jobs"
  | "career_stack_role_selected"
  | "career_stack_role_interest"
  | "floating_cta_shown"
  | "floating_cta_clicked"
  | "floating_cta_state";

type EventProps = Record<string, string | number | boolean | null | undefined>;

export function track(event: TrackEvent, props: EventProps = {}) {
  try {
    if (typeof window === "undefined") return;
    const safe = redactDeep(props);
    const w = window as unknown as { dataLayer?: unknown[] };
    w.dataLayer = w.dataLayer || [];
    // page_access följer med varje event så GTM kan villkora utlösaren utan
    // att matcha sökvägar.
    w.dataLayer.push({ event, page_access: pageAccess(window.location.pathname), ...safe });
    if (import.meta.env.DEV) {
      // eslint-disable-next-line no-console
      console.debug("[analytics]", event, safe);
    }

  } catch {
    // no-op
  }
}

/**
 * GA4-event. Skickar via `gtag('event', name, params)` när GA4-skriptet är laddat
 * (efter cookie-samtycke), annars köas i dataLayer så GTM/GA4 kan plocka upp
 * eventet så snart det initieras. Sväljer alla fel — får aldrig krascha UI.
 * Skicka ALDRIG PII eller fritextfält. Sök-termer skickas som `search_term`
 * enligt GA4:s rekommenderade parameternamn.
 */
type Ga4Params = Record<string, string | number | boolean | null | undefined>;

export function trackGa4(eventName: string, params: Ga4Params = {}) {
  try {
    if (typeof window === "undefined") return;
    const w = window as unknown as { dataLayer?: unknown[] };
    // Trimma undefined så GA4-rapporten inte fylls med tomma parametrar,
    // och maskera känsliga URL-parametrar (page_location m.fl.).
    const clean: Ga4Params = {};
    for (const [k, v] of Object.entries(redactDeep(params))) {
      if (v !== undefined && v !== null && v !== "") clean[k] = v as Ga4Params[string];
    }

    // GTM äger utskicket: vi pushar ett vanligt objekt med `event`-nyckeln så
    // att containern kan bygga en anpassad utlösare på namnet. Ingen direkt
    // gtag('event', …) längre — GA4-taggen bor i GTM och skulle annars
    // dubbelräkna mot en kodladdad instans.
    w.dataLayer = w.dataLayer || [];
    w.dataLayer.push({ event: eventName, ...clean });
    if (import.meta.env.DEV) {
      // eslint-disable-next-line no-console
      console.debug("[ga4]", eventName, clean);
    }
  } catch {
    // no-op
  }
}
